import { OpenStreetMapProvider } from 'leaflet-geosearch';
import { interleave, loadPostcodePostort, searchStreetPrefix } from './streetSearch.js';

// Kartvy's place search: Nominatim limited to Sweden, with short labels, plus the streets that start with what's
// typed (Photon, streetSearch.js), since Nominatim only matches whole words: "hudding" finds Huddingevägen.
// Nominatim's own label lists every level ("Karlsrogatan, Sommarro, Kungsgärdet, Uppsala, Uppsala kommun,
// Uppsala län, 752 39, Sverige"); we show the street or place plus its ort only ("Karlsrogatan 81, Uppsala").
// A kommun or län isn't a place here: the search's own Kommun / Län rows cover them.
const AREA_TYPES = new Set(['municipality', 'county', 'state', 'country']);

const ORT_KEYS = ['city', 'town', 'village', 'hamlet'];

const firstPart = (displayName) => String(displayName || '').split(',')[0].trim();

export const buildShortLabel = (raw) => {
    const address = raw?.address || {};
    let primary;
    if (address.road && address.house_number) {
        primary = `${address.road} ${address.house_number}`;
    } else {
        primary = String(raw?.name || '').trim() || firstPart(raw?.display_name);
    }
    const ortKey = ORT_KEYS.find((key) => address[key]);
    const ort = ortKey ? address[ortKey] : address.municipality;
    // A city, kommun or län is its own context.
    if (!ort || ort === primary || ['city', 'town', 'municipality', 'county', 'state'].includes(raw?.addresstype)) {
        return primary || raw?.display_name || '';
    }
    return `${primary}, ${ort}`;
};

export class SwedenSearchProvider extends OpenStreetMapProvider {
    constructor(options = {}) {
        super({
            ...options,
            // Swedish names whatever the browser language ("Göteborg", not "Gothenburg").
            params: { countrycodes: 'se', addressdetails: 1, 'accept-language': 'sv', ...(options.params || {}) },
        });
    }

    parse(response) {
        return super.parse(response)
            .filter((result) => !AREA_TYPES.has(result.raw?.addresstype))
            .map((result) => ({ ...result, label: buildShortLabel(result.raw) || result.label }));
    }

    // Nominatim's places and Photon's streets together: the streets that start with the text first, or, with a
    // house number typed, the two in turn, Nominatim's addresses first. Either failing leaves the other's.
    async search(options) {
        const query = String(options?.query || '').trim();
        const [places, streets, postorter] = await Promise.allSettled([
            super.search(options),
            searchStreetPrefix(query),
            loadPostcodePostort(),
        ]);
        if (places.status === 'rejected' && streets.status === 'rejected') throw places.reason;
        const byPostcode = postorter.status === 'fulfilled' ? postorter.value : new Map();
        const streetResults = (streets.status === 'fulfilled' ? streets.value : []).map((street) => {
            const place = byPostcode.get(street.postcode) || street.town;
            return {
                x: street.lng,
                y: street.lat,
                label: place ? `${street.road}, ${place}` : street.road,
                bounds: street.bounds,
                raw: { address: { road: street.road, postcode: street.postcode, city: place }, addresstype: 'road' },
            };
        });
        const placeResults = places.status === 'fulfilled' ? places.value : [];
        const ordered = /\d/.test(query) ? interleave(placeResults, streetResults) : [...streetResults, ...placeResults];
        // The same street often comes back once per postcode or from both; keep the first.
        const seen = new Set();
        return ordered.filter((result) => {
            const key = String(result.label || '').toLowerCase();
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }
}
